import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Badge } from '../components/ui/Badge';
import { Search, PlusCircle, ArrowLeft, Calendar, Clock, FileIcon, Send, MessageSquare, XCircle, ChevronDown, ChevronUp, Check, Zap, Trash2, CornerDownLeft, UploadCloudIcon, ImageIcon, X, ClipboardIcon, FileTextIcon, FileArchiveIcon } from 'lucide-react';
import { mockTickets, statusConfig, priorityConfig, categoryConfig, SupportTicket, TicketStatus, TicketPriority, TicketCategory } from '../data/supportData';
function CustomSelect({
  label,
  placeholder,
  value,
  onChange,
  options,
  renderOption
}: {
  label: string;
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
  options: {
    value: string;
    label: string;
  }[];
  renderOption?: (opt: {
    value: string;
    label: string;
  }) => React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);
  const selected = options.find(o => o.value === value);
  const displayLabel = selected && selected.value !== '' ? selected.label : placeholder || 'Select...';
  return <div ref={ref} className="relative" data-id="element-7642">
      <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-7643">
        {label}
      </label>
      <button type="button" onClick={() => setOpen(!open)} className={`w-full flex items-center justify-between rounded-lg border px-3 py-2.5 text-sm transition-all ${open ? 'border-primary-500 ring-2 ring-primary-500/20 bg-white' : 'border-slate-300 bg-white hover:border-slate-400'} ${!selected || selected.value === '' ? 'text-slate-400' : 'text-slate-900'}`} data-id="element-7644">
        <span className="truncate" data-id="element-7645">
          {renderOption && selected && selected.value !== '' ? renderOption(selected) : displayLabel}
        </span>
        <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform flex-shrink-0 ml-2 ${open ? 'rotate-180' : ''}`} data-id="element-7646" />
      </button>

      {open && <div className="absolute z-50 mt-1.5 w-full bg-white border border-slate-200 rounded-lg shadow-lg py-1 max-h-56 overflow-y-auto animate-in fade-in slide-in-from-top-1 duration-150" data-id="element-7647">
          {options.map(opt => {
        const isSelected = opt.value === value;
        return <button key={opt.value} type="button" onClick={() => {
          onChange(opt.value);
          setOpen(false);
        }} className={`w-full flex items-center justify-between px-3 py-2.5 text-sm transition-colors ${isSelected ? 'bg-primary-50 text-primary-700' : 'text-slate-700 hover:bg-slate-50'}`} data-id="element-7648">
                <span data-id="element-7649">{renderOption ? renderOption(opt) : opt.label}</span>
                {isSelected && <Check className="h-4 w-4 text-primary-600 flex-shrink-0" data-id="element-7650" />}
              </button>;
      })}
        </div>}
    </div>;
}
type AttachedFile = {
  id: string;
  file: File;
  preview?: string;
};
function FileDropZone({
  files,
  onFilesChange,
  compact = false
}: {
  files: AttachedFile[];
  onFilesChange: (files: AttachedFile[]) => void;
  compact?: boolean;
}) {
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropRef = useRef<HTMLDivElement>(null);
  const dragCounter = useRef(0);
  const ACCEPTED = '.png,.jpg,.jpeg,.webp,.gif,.pdf,.zip,.doc,.docx';
  const MAX_SIZE = 5 * 1024 * 1024; // 5MB
  const processFiles = (incoming: FileList | File[]) => {
    const newFiles: AttachedFile[] = Array.from(incoming).filter(f => f.size <= MAX_SIZE).map(f => {
      const af: AttachedFile = {
        id: `f-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        file: f
      };
      if (f.type.startsWith('image/')) {
        af.preview = URL.createObjectURL(f);
      }
      return af;
    });
    onFilesChange([...files, ...newFiles]);
  };
  const removeFile = (id: string) => {
    const file = files.find(f => f.id === id);
    if (file?.preview) URL.revokeObjectURL(file.preview);
    onFilesChange(files.filter(f => f.id !== id));
  };
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    if (e.dataTransfer.items?.length) setIsDragging(true);
  };
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) setIsDragging(false);
  };
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounter.current = 0;
    if (e.dataTransfer.files?.length) processFiles(e.dataTransfer.files);
  };
  // Clipboard paste — listen on document so it works anywhere (modal open, detail view, etc.)
  useEffect(() => {
    const handlePaste = (e: ClipboardEvent) => {
      // Don't intercept if user is pasting into a text input/textarea
      const active = document.activeElement;
      if (active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA') && !(active as HTMLInputElement).readOnly) {
        // Only intercept if the clipboard has files (images), not text
        const hasFiles = e.clipboardData?.types?.includes('Files');
        if (!hasFiles) return;
      }
      const items = e.clipboardData?.items;
      if (!items) return;
      const pastedFiles: File[] = [];
      for (let i = 0; i < items.length; i++) {
        if (items[i].kind === 'file') {
          const f = items[i].getAsFile();
          if (f) pastedFiles.push(f);
        }
      }
      if (pastedFiles.length) {
        e.preventDefault();
        processFiles(pastedFiles);
      }
    };
    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  });
  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };
  const getFileIcon = (type: string) => {
    if (type.startsWith('image/')) return <ImageIcon className="h-4 w-4 text-blue-500" data-id="element-7651" />;
    if (type === 'application/pdf') return <FileTextIcon className="h-4 w-4 text-red-500" data-id="element-7652" />;
    if (type.includes('zip') || type.includes('archive')) return <FileArchiveIcon className="h-4 w-4 text-amber-500" data-id="element-7653" />;
    return <FileIcon className="h-4 w-4 text-slate-400" data-id="element-7654" />;
  };
  return <div ref={dropRef} onDragEnter={handleDragEnter} onDragLeave={handleDragLeave} onDragOver={handleDragOver} onDrop={handleDrop} className="outline-none" data-id="element-7655">
      <input ref={inputRef} type="file" multiple accept={ACCEPTED} className="hidden" onChange={e => {
      if (e.target.files?.length) processFiles(e.target.files);
      e.target.value = '';
    }} data-id="element-7656" />

      {/* Drop zone */}
      <div className={`relative border-2 border-dashed rounded-lg transition-all ${compact ? 'p-4' : 'p-6'} ${isDragging ? 'border-primary-400 bg-primary-50 scale-[1.01]' : 'border-slate-200 bg-slate-50'}`} data-id="element-7657">
        {isDragging && <div className="absolute inset-0 flex items-center justify-center bg-primary-50/80 rounded-lg z-10" data-id="element-7658">
            <div className="flex flex-col items-center gap-1.5" data-id="element-7659">
              <UploadCloudIcon className="h-8 w-8 text-primary-500 animate-bounce" data-id="element-7660" />
              <span className="text-sm font-medium text-primary-700" data-id="element-7661">
                Drop files here
              </span>
            </div>
          </div>}
        <div className="flex flex-col items-center justify-center text-center" data-id="element-7662">
          <UploadCloudIcon className={`text-slate-400 mb-2 ${compact ? 'h-5 w-5' : 'h-6 w-6'}`} data-id="element-7663" />
          <p className={`font-medium text-slate-700 ${compact ? 'text-xs' : 'text-sm'}`} data-id="element-7664">
            Drop files,{' '}
            <button type="button" onClick={() => inputRef.current?.click()} className="text-primary-600 underline underline-offset-2 hover:text-primary-700 cursor-pointer" data-id="element-7665">
              browse
            </button>
            , or paste
          </p>
          <p className={`text-slate-400 mt-0.5 ${compact ? 'text-[10px]' : 'text-xs'}`} data-id="element-7666">
            PNG, JPG, PDF, ZIP up to 5 MB
          </p>
          <div className="flex items-center gap-1.5 mt-1.5" data-id="element-7667">
            <ClipboardIcon className="h-3 w-3 text-slate-300" data-id="element-7668" />
            <span className="text-[10px] text-slate-400" data-id="element-7669">
              <kbd className="px-1 py-0.5 bg-slate-100 border border-slate-200 rounded text-[9px] font-mono" data-id="element-7670">
                ⌘V
              </kbd>{' '}
              to paste images anywhere
            </span>
          </div>
        </div>
      </div>

      {/* File list */}
      {files.length > 0 && <div className="mt-2 space-y-1.5" data-id="element-7671">
          {files.map(af => <div key={af.id} className="flex items-center gap-2.5 px-3 py-2 bg-white border border-slate-200 rounded-lg group" data-id="element-7672">
              {af.preview ? <img src={af.preview} alt={af.file.name} className="h-9 w-9 rounded object-cover flex-shrink-0 border border-slate-100" data-id="element-7673" /> : <div className="h-9 w-9 rounded bg-slate-50 border border-slate-100 flex items-center justify-center flex-shrink-0" data-id="element-7674">
                  {getFileIcon(af.file.type)}
                </div>}
              <div className="flex-1 min-w-0" data-id="element-7675">
                <p className="text-xs font-medium text-slate-700 truncate" data-id="element-7676">
                  {af.file.name}
                </p>
                <p className="text-[10px] text-slate-400" data-id="element-7677">
                  {formatSize(af.file.size)}
                </p>
              </div>
              <button type="button" onClick={e => {
          e.stopPropagation();
          removeFile(af.id);
        }} className="h-6 w-6 flex items-center justify-center rounded-full text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors opacity-0 group-hover:opacity-100 flex-shrink-0" data-id="element-7678">
                <X className="h-3.5 w-3.5" data-id="element-7679" />
              </button>
            </div>)}
        </div>}
    </div>;
}
export function SupportPage() {
  const [view, setView] = useState<'list' | 'detail'>('list');
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
  const [showNewModal, setShowNewModal] = useState(false);
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | TicketStatus>('all');
  const [priorityFilter, setPriorityFilter] = useState<'all' | TicketPriority>('all');
  // Local state for tickets (to allow adding new ones)
  const [tickets, setTickets] = useState<SupportTicket[]>(mockTickets.filter(t => t.builderId === 'BLD-001'));
  // New Request Form State
  const [modalMode, setModalMode] = useState<'single' | 'bulk'>('single');
  const [newRequest, setNewRequest] = useState({
    title: '',
    category: '' as TicketCategory,
    priority: 'medium' as TicketPriority,
    description: ''
  });
  // Bulk mode state
  type BulkRequestItem = {
    id: string;
    title: string;
    description: string;
    category: TicketCategory | 'default';
    priority: TicketPriority | 'default';
    expanded: boolean;
  };
  const emptyBulkItem = (): BulkRequestItem => ({
    id: `bulk-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    title: '',
    description: '',
    category: 'default',
    priority: 'default',
    expanded: false
  });
  const [bulkItems, setBulkItems] = useState<BulkRequestItem[]>([emptyBulkItem()]);
  const [bulkCategory, setBulkCategory] = useState<TicketCategory>('');
  const [bulkPriority, setBulkPriority] = useState<TicketPriority>('medium');
  const bulkInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const updateBulkItem = (id: string, updates: Partial<BulkRequestItem>) => {
    setBulkItems(prev => prev.map(item => item.id === id ? {
      ...item,
      ...updates
    } : item));
  };
  const addBulkRow = (afterId?: string): string => {
    const newItem = emptyBulkItem();
    setBulkItems(prev => {
      if (!afterId) return [...prev, newItem];
      const idx = prev.findIndex(i => i.id === afterId);
      const next = [...prev];
      next.splice(idx + 1, 0, newItem);
      return next;
    });
    setTimeout(() => bulkInputRefs.current[newItem.id]?.focus(), 50);
    return newItem.id;
  };
  const removeBulkRow = (id: string) => {
    setBulkItems(prev => {
      if (prev.length <= 1) return [emptyBulkItem()];
      const idx = prev.findIndex(i => i.id === id);
      const next = prev.filter(i => i.id !== id);
      const focusIdx = Math.min(idx, next.length - 1);
      setTimeout(() => bulkInputRefs.current[next[focusIdx]?.id]?.focus(), 50);
      return next;
    });
  };
  const handleBulkKeyDown = (e: React.KeyboardEvent, item: BulkRequestItem, index: number) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addBulkRow(item.id);
    }
    if (e.key === 'Tab' && !e.shiftKey && index === bulkItems.length - 1 && item.title.trim()) {
      e.preventDefault();
      addBulkRow(item.id);
    }
    if (e.key === 'Backspace' && !item.title && bulkItems.length > 1) {
      e.preventDefault();
      removeBulkRow(item.id);
    }
    if (e.key === 'ArrowDown' && item.title.trim() && !item.expanded) {
      e.preventDefault();
      updateBulkItem(item.id, {
        expanded: true
      });
    }
    if (e.key === 'ArrowUp' && item.expanded) {
      e.preventDefault();
      updateBulkItem(item.id, {
        expanded: false
      });
    }
  };
  const validBulkItems = bulkItems.filter(i => i.title.trim());
  const handleBulkSubmitAll = () => {
    const newTickets: SupportTicket[] = validBulkItems.map(item => ({
      id: `REQ-00${Math.floor(1000 + Math.random() * 9000)}`,
      title: item.title.trim(),
      description: item.description.trim() || item.title.trim(),
      status: 'open' as const,
      priority: item.priority === 'default' ? bulkPriority : item.priority,
      category: item.category === 'default' ? bulkCategory : item.category,
      builderId: 'BLD-001',
      builderName: 'Mattamy Homes',
      createdDate: new Date().toISOString(),
      updatedDate: new Date().toISOString(),
      unreadByBuilder: false,
      unreadByAdmin: true,
      messages: [{
        id: `msg-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        senderId: 'b-1',
        senderName: 'Jane Smith',
        senderType: 'builder' as const,
        senderInitials: 'JS',
        content: item.description.trim() || item.title.trim(),
        timestamp: new Date().toISOString()
      }]
    }));
    setTickets(prev => [...newTickets, ...prev]);
    setShowNewModal(false);
    setBulkItems([emptyBulkItem()]);
    setBulkCategory('');
    setBulkPriority('medium');
    setModalMode('single');
  };
  const handleModeSwitch = (mode: 'single' | 'bulk') => {
    setModalMode(mode);
    if (mode === 'bulk') {
      setBulkItems([emptyBulkItem()]);
      setBulkCategory(newRequest.category);
      setBulkPriority(newRequest.priority);
    }
  };
  // File attachment state
  const [modalFiles, setModalFiles] = useState<AttachedFile[]>([]);
  const [replyFiles, setReplyFiles] = useState<AttachedFile[]>([]);
  // Reply Form State
  const [replyContent, setReplyContent] = useState('');
  const filteredTickets = useMemo(() => {
    return tickets.filter(ticket => {
      const matchesSearch = ticket.title.toLowerCase().includes(searchQuery.toLowerCase()) || ticket.id.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = statusFilter === 'all' || ticket.status === statusFilter;
      const matchesPriority = priorityFilter === 'all' || ticket.priority === priorityFilter;
      return matchesSearch && matchesStatus && matchesPriority;
    });
  }, [tickets, searchQuery, statusFilter, priorityFilter]);
  const handleViewTicket = (ticket: SupportTicket) => {
    // Mark as read
    if (ticket.unreadByBuilder) {
      setTickets(prev => prev.map(t => t.id === ticket.id ? {
        ...t,
        unreadByBuilder: false
      } : t));
      ticket.unreadByBuilder = false;
    }
    setSelectedTicket(ticket);
    setView('detail');
  };
  const handleBackToList = () => {
    setSelectedTicket(null);
    setView('list');
  };
  const handleSubmitNewRequest = (e: React.FormEvent) => {
    e.preventDefault();
    const newTicket: SupportTicket = {
      id: `REQ-00${Math.floor(1000 + Math.random() * 9000)}`,
      title: newRequest.title,
      description: newRequest.description,
      status: 'open',
      priority: newRequest.priority,
      category: newRequest.category,
      builderId: 'BLD-001',
      builderName: 'Mattamy Homes',
      createdDate: new Date().toISOString(),
      updatedDate: new Date().toISOString(),
      unreadByBuilder: false,
      unreadByAdmin: true,
      messages: [{
        id: `msg-${Date.now()}`,
        senderId: 'b-1',
        senderName: 'Jane Smith',
        senderType: 'builder',
        senderInitials: 'JS',
        content: newRequest.description,
        timestamp: new Date().toISOString()
      }]
    };
    setTickets([newTicket, ...tickets]);
    setShowNewModal(false);
    setModalFiles([]);
    setNewRequest({
      title: '',
      category: '',
      priority: 'medium',
      description: ''
    });
  };
  const handleSendReply = () => {
    if (!replyContent.trim() || !selectedTicket) return;
    const newMessage = {
      id: `msg-${Date.now()}`,
      senderId: 'b-1',
      senderName: 'Jane Smith',
      senderType: 'builder',
      senderInitials: 'JS',
      content: replyContent,
      timestamp: new Date().toISOString()
    };
    const updatedTicket = {
      ...selectedTicket,
      updatedDate: new Date().toISOString(),
      unreadByAdmin: true,
      messages: [...selectedTicket.messages, newMessage]
    };
    setTickets(prev => prev.map(t => t.id === selectedTicket.id ? updatedTicket : t));
    setSelectedTicket(updatedTicket);
    setReplyContent('');
    setReplyFiles([]);
  };
  if (view === 'detail' && selectedTicket) {
    const StatusIcon = statusConfig[selectedTicket.status].icon;
    return <div className="max-w-5xl mx-auto space-y-6 pb-12" data-id="element-7680">
        <button onClick={handleBackToList} className="flex items-center text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors" data-id="element-7681">
          <ArrowLeft className="h-4 w-4 mr-1" data-id="element-7682" />
          Back to Requests
        </button>

        <div className="flex flex-col md:flex-row md:items-start justify-between gap-4" data-id="element-7683">
          <div data-id="element-7684">
            <div className="flex items-center gap-3 mb-2" data-id="element-7685">
              <h1 className="text-2xl font-bold text-slate-900" data-id="element-7686">
                {selectedTicket.id} | {selectedTicket.title}
              </h1>
              <Badge variant={statusConfig[selectedTicket.status].variant} data-id="element-7687">
                <div className="flex items-center gap-1" data-id="element-7688">
                  <StatusIcon className="h-3 w-3" data-id="element-7689" />
                  {statusConfig[selectedTicket.status].label}
                </div>
              </Badge>
              <Badge variant={priorityConfig[selectedTicket.priority].variant} data-id="element-7690">
                {priorityConfig[selectedTicket.priority].label} Priority
              </Badge>
            </div>
            <div className="flex items-center gap-4 text-sm text-slate-500" data-id="element-7691">
              <span className="flex items-center gap-1.5" data-id="element-7692">
                <Calendar className="h-4 w-4" data-id="element-7693" />
                Created{' '}
                {new Date(selectedTicket.createdDate).toLocaleDateString()}
              </span>
              <span className="flex items-center gap-1.5" data-id="element-7694">
                <Clock className="h-4 w-4" data-id="element-7695" />
                Last activity:{' '}
                {new Date(selectedTicket.updatedDate).toLocaleDateString()}
              </span>
            </div>
          </div>
        </div>

        <Card data-id="element-7696">
          <CardHeader className="bg-slate-50 border-b border-slate-100 pb-4" data-id="element-7697">
            <CardTitle className="text-base" data-id="element-7698">Description</CardTitle>
          </CardHeader>
          <CardContent className="pt-6" data-id="element-7699">
            <div className="bg-slate-50 rounded-lg p-4 text-slate-700 whitespace-pre-wrap" data-id="element-7700">
              {selectedTicket.description}
            </div>
          </CardContent>
        </Card>

        <Card data-id="element-7701">
          <CardContent className="p-0" data-id="element-7702">
            <div className="divide-y divide-slate-100" data-id="element-7703">
              {selectedTicket.messages.filter(m => !m.isInternal).map(msg => <div key={msg.id} className="p-6 flex gap-4" data-id="element-7704">
                    <div className={`h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold ${msg.senderType === 'builder' ? 'bg-primary-100 text-primary-700' : 'bg-slate-800 text-white'}`} data-id="element-7705">
                      {msg.senderInitials}
                    </div>
                    <div className="flex-1 space-y-2" data-id="element-7706">
                      <div className="flex items-baseline justify-between" data-id="element-7707">
                        <span className="font-semibold text-slate-900" data-id="element-7708">
                          {msg.senderName}
                        </span>
                        <span className="text-xs text-slate-500" data-id="element-7709">
                          {new Date(msg.timestamp).toLocaleString([], {
                      dateStyle: 'medium',
                      timeStyle: 'short'
                    })}
                        </span>
                      </div>
                      <div className="text-slate-700 whitespace-pre-wrap" data-id="element-7710">
                        {msg.content}
                      </div>
                      {msg.attachments && msg.attachments.length > 0 && <div className="flex flex-wrap gap-2 pt-2" data-id="element-7711">
                          {msg.attachments.map((file, i) => <div key={i} className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-md text-xs font-medium text-slate-600" data-id="element-7712">
                              <FileIcon className="h-3.5 w-3.5 text-slate-400" data-id="element-7713" />
                              {file.name}
                            </div>)}
                        </div>}
                    </div>
                  </div>)}
            </div>
          </CardContent>
        </Card>

        <Card data-id="element-7714">
          <CardHeader className="pb-4" data-id="element-7715">
            <CardTitle className="text-base" data-id="element-7716">Additional Comments</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4" data-id="element-7717">
            <textarea value={replyContent} onChange={e => setReplyContent(e.target.value)} placeholder="Add notes for the support team..." className="w-full min-h-[120px] p-3 border border-slate-300 rounded-lg focus:ring-primary-500 focus:border-primary-500 resize-y" data-id="element-7718" />

            <div data-id="element-7719">
              <p className="text-sm font-medium text-slate-700 mb-2" data-id="element-7720">
                Attachments
              </p>
              <FileDropZone files={replyFiles} onFilesChange={setReplyFiles} data-id="element-7721" />
            </div>

            <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-sm text-blue-800" data-id="element-7722">
              The support team will be notified of your response and can view
              your notes.
            </div>

            <div className="flex justify-end pt-2" data-id="element-7723">
              <Button onClick={handleSendReply} disabled={!replyContent.trim()} leftIcon={<Send className="h-4 w-4" data-id="element-7725" />} data-id="element-7724">
                Send Response
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>;
  }
  return <div className="max-w-7xl mx-auto space-y-6 pb-12" data-id="element-7726">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-7727">
        <div data-id="element-7728">
          <h1 className="text-2xl font-bold text-slate-900" data-id="element-7729">
            Your Support Requests
          </h1>
          <p className="text-slate-500" data-id="element-7730">Review and manage your requests</p>
        </div>
        <Button onClick={() => setShowNewModal(true)} leftIcon={<PlusCircle className="h-4 w-4" data-id="element-7732" />} data-id="element-7731">
          Submit Request
        </Button>
      </div>

      <Card noPadding data-id="element-7733">
        <div className="p-4 border-b border-slate-200 bg-slate-50 rounded-t-xl flex flex-col sm:flex-row gap-3" data-id="element-7734">
          <div className="flex-1 relative" data-id="element-7735">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" data-id="element-7736" />
            <input type="text" placeholder="Search by ID, title or description..." className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-md bg-white focus:ring-primary-500 focus:border-primary-500" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-7737" />
          </div>
          <div className="flex gap-3" data-id="element-7738">
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value as any)} className="px-4 py-2 border border-slate-300 rounded-md bg-white focus:ring-primary-500 focus:border-primary-500 min-w-[140px]" data-id="element-7739">
              <option value="all" data-id="element-7740">All Status</option>
              {Object.entries(statusConfig).map(([key, config]) => <option key={key} value={key} data-id="element-7741">
                  {config.label}
                </option>)}
            </select>
            <select value={priorityFilter} onChange={e => setPriorityFilter(e.target.value as any)} className="px-4 py-2 border border-slate-300 rounded-md bg-white focus:ring-primary-500 focus:border-primary-500 min-w-[140px]" data-id="element-7742">
              <option value="all" data-id="element-7743">All Priorities</option>
              {Object.entries(priorityConfig).map(([key, config]) => <option key={key} value={key} data-id="element-7744">
                  {config.label}
                </option>)}
            </select>
          </div>
        </div>

        <div className="overflow-x-auto" data-id="element-7745">
          <table className="w-full text-left text-sm" data-id="element-7746">
            <thead className="bg-slate-50 text-slate-500 font-medium border-b border-slate-200" data-id="element-7747">
              <tr data-id="element-7748">
                <th className="px-6 py-4" data-id="element-7749">ID</th>
                <th className="px-6 py-4" data-id="element-7750">Title</th>
                <th className="px-6 py-4" data-id="element-7751">Status</th>
                <th className="px-6 py-4" data-id="element-7752">Priority</th>
                <th className="px-6 py-4" data-id="element-7753">Created</th>
                <th className="px-6 py-4" data-id="element-7754">Updated</th>
                <th className="px-6 py-4 text-right" data-id="element-7755">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-7756">
              {filteredTickets.length === 0 ? <tr data-id="element-7757">
                  <td colSpan={7} className="px-6 py-12 text-center text-slate-500" data-id="element-7758">
                    <MessageSquare className="h-8 w-8 mx-auto mb-3 text-slate-300" data-id="element-7759" />
                    <p data-id="element-7760">No requests found matching your filters.</p>
                  </td>
                </tr> : filteredTickets.map(ticket => {
              const StatusIcon = statusConfig[ticket.status].icon;
              return <tr key={ticket.id} className="hover:bg-slate-50 transition-colors" data-id="element-7761">
                      <td className="px-6 py-4 font-medium text-slate-900" data-id="element-7762">
                        <div className="flex items-center gap-2" data-id="element-7763">
                          {ticket.unreadByBuilder && <span className="h-2 w-2 rounded-full bg-primary-500" data-id="element-7764"></span>}
                          {ticket.id}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-slate-900 max-w-xs truncate" title={ticket.title} data-id="element-7765">
                        {ticket.title}
                      </td>
                      <td className="px-6 py-4" data-id="element-7766">
                        <Badge variant={statusConfig[ticket.status].variant} data-id="element-7767">
                          <div className="flex items-center gap-1" data-id="element-7768">
                            <StatusIcon className="h-3 w-3" data-id="element-7769" />
                            {statusConfig[ticket.status].label}
                          </div>
                        </Badge>
                      </td>
                      <td className="px-6 py-4" data-id="element-7770">
                        <Badge variant={priorityConfig[ticket.priority].variant} data-id="element-7771">
                          {priorityConfig[ticket.priority].label}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 text-slate-600" data-id="element-7772">
                        {new Date(ticket.createdDate).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 text-slate-600" data-id="element-7773">
                        {new Date(ticket.updatedDate).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 text-right" data-id="element-7774">
                        <Button variant="ghost" size="sm" onClick={() => handleViewTicket(ticket)} data-id="element-7775">
                          View Details
                        </Button>
                      </td>
                    </tr>;
            })}
            </tbody>
          </table>
        </div>

        {filteredTickets.length > 0 && <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between bg-slate-50 rounded-b-xl" data-id="element-7776">
            <div className="flex items-center gap-2 text-sm text-slate-500" data-id="element-7777">
              <span data-id="element-7778">Items per page:</span>
              <select className="border-slate-300 rounded-md py-1 pl-2 pr-6 text-sm" data-id="element-7779">
                <option data-id="element-7780">10</option>
                <option data-id="element-7781">25</option>
                <option data-id="element-7782">50</option>
              </select>
            </div>
            <div className="flex items-center gap-4 text-sm text-slate-600" data-id="element-7783">
              <span data-id="element-7784">
                1 - {filteredTickets.length} of {filteredTickets.length}
              </span>
              <div className="flex gap-1" data-id="element-7785">
                <Button variant="ghost" size="sm" disabled className="px-2" data-id="element-7786">
                  &lt;
                </Button>
                <Button variant="ghost" size="sm" disabled className="px-2" data-id="element-7787">
                  &gt;
                </Button>
              </div>
            </div>
          </div>}
      </Card>

      {/* New Request Modal */}
      {showNewModal && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-id="element-7788">
          <div className={`bg-white rounded-xl shadow-xl w-full max-h-[90vh] flex flex-col ${modalMode === 'bulk' ? 'max-w-2xl' : 'max-w-lg'}`} data-id="element-7789">
            {/* Header */}
            <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center flex-shrink-0" data-id="element-7790">
              <div data-id="element-7791">
                <h2 className="text-lg font-bold text-slate-900" data-id="element-7792">
                  {modalMode === 'single' ? 'Submit a New Request' : 'Bulk Submit Requests'}
                </h2>
                <p className="text-sm text-slate-500" data-id="element-7793">
                  {modalMode === 'single' ? 'Describe your issue or request' : 'Quickly submit multiple requests at once'}
                </p>
              </div>
              <button onClick={() => {
            setShowNewModal(false);
            setModalMode('single');
          }} className="text-slate-400 hover:text-slate-600" data-id="element-7794">
                <XCircle className="h-5 w-5" data-id="element-7795" />
              </button>
            </div>

            {/* Mode Toggle */}
            <div className="px-6 pt-4 flex-shrink-0" data-id="element-7796">
              <div className="flex items-center bg-slate-100 rounded-lg p-0.5 w-fit" data-id="element-7797">
                <button type="button" onClick={() => handleModeSwitch('single')} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${modalMode === 'single' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-7798">
                  <PlusCircle className="h-3.5 w-3.5" data-id="element-7799" /> Single
                </button>
                <button type="button" onClick={() => handleModeSwitch('bulk')} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${modalMode === 'bulk' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-7800">
                  <Zap className="h-3.5 w-3.5" data-id="element-7801" /> Bulk Add
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-6 py-5" data-id="element-7802">
              {modalMode === 'single' ? <form onSubmit={handleSubmitNewRequest} id="single-form" className="space-y-5" data-id="element-7803">
                  <div data-id="element-7804">
                    <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-7805">
                      Title <span className="text-red-500" data-id="element-7806">*</span>
                    </label>
                    <Input placeholder="Brief summary of your request" value={newRequest.title} onChange={e => setNewRequest({
                ...newRequest,
                title: e.target.value
              })} required data-id="element-7807" />
                  </div>

                  <div className="grid grid-cols-2 gap-4" data-id="element-7808">
                    <CustomSelect label="Category" placeholder="Select category (optional)" value={newRequest.category} onChange={val => setNewRequest({
                ...newRequest,
                category: val as TicketCategory
              })} options={[{
                value: '',
                label: 'None'
              }, ...Object.entries(categoryConfig).map(([key, label]) => ({
                value: key,
                label
              }))]} data-id="element-7809" />
                    <CustomSelect label="Priority" value={newRequest.priority} onChange={val => setNewRequest({
                ...newRequest,
                priority: val as TicketPriority
              })} options={Object.entries(priorityConfig).map(([key, config]) => ({
                value: key,
                label: config.label
              }))} renderOption={opt => {
                const colors: Record<string, string> = {
                  low: 'bg-slate-100 text-slate-700',
                  medium: 'bg-blue-100 text-blue-700',
                  high: 'bg-amber-100 text-amber-700',
                  urgent: 'bg-red-100 text-red-700'
                };
                return <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${colors[opt.value] || ''}`} data-id="element-7811">
                            {opt.label}
                          </span>;
              }} data-id="element-7810" />
                  </div>

                  <div data-id="element-7812">
                    <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-7813">
                      Description <span className="text-red-500" data-id="element-7814">*</span>
                    </label>
                    <textarea rows={5} className="w-full rounded-md border-slate-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm resize-y" placeholder="Please provide as much detail as possible..." value={newRequest.description} onChange={e => setNewRequest({
                ...newRequest,
                description: e.target.value
              })} required data-id="element-7815" />
                  </div>

                  <div data-id="element-7816">
                    <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-7817">
                      Attachments
                    </label>
                    <FileDropZone files={modalFiles} onFilesChange={setModalFiles} data-id="element-7818" />
                  </div>
                </form> : <div className="space-y-5" data-id="element-7819">
                  {/* Shared category & priority */}
                  <div className="grid grid-cols-2 gap-4" data-id="element-7820">
                    <CustomSelect label="Category (all requests)" placeholder="Select category (optional)" value={bulkCategory} onChange={val => setBulkCategory(val as TicketCategory)} options={[{
                value: '',
                label: 'None'
              }, ...Object.entries(categoryConfig).map(([key, label]) => ({
                value: key,
                label
              }))]} data-id="element-7821" />
                    <CustomSelect label="Priority (all requests)" value={bulkPriority} onChange={val => setBulkPriority(val as TicketPriority)} options={Object.entries(priorityConfig).map(([key, config]) => ({
                value: key,
                label: config.label
              }))} renderOption={opt => {
                const colors: Record<string, string> = {
                  low: 'bg-slate-100 text-slate-700',
                  medium: 'bg-blue-100 text-blue-700',
                  high: 'bg-amber-100 text-amber-700',
                  urgent: 'bg-red-100 text-red-700'
                };
                return <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${colors[opt.value] || ''}`} data-id="element-7823">
                            {opt.label}
                          </span>;
              }} data-id="element-7822" />
                  </div>

                  <div className="border-t border-slate-100 pt-4" data-id="element-7824">
                    {/* Column headers */}
                    <div className="flex items-center gap-2 px-1 mb-2" data-id="element-7825">
                      <span className="w-5" data-id="element-7826" />
                      <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider flex-1" data-id="element-7827">
                        Request Title
                      </span>
                      <span className="w-7" data-id="element-7828" />
                      <span className="w-7" data-id="element-7829" />
                    </div>

                    {/* Repeater rows */}
                    <div className="space-y-1" data-id="element-7830">
                      {bulkItems.map((item, index) => <div key={item.id} data-id="element-7831">
                          <div className="flex items-center gap-2 group relative" data-id="element-7832">
                            <span className="text-[10px] text-slate-300 w-5 text-right tabular-nums flex-shrink-0" data-id="element-7833">
                              {index + 1}
                            </span>
                            <div className="flex-1 relative" data-id="element-7834">
                              <input ref={el => {
                        bulkInputRefs.current[item.id] = el;
                      }} type="text" value={item.title} onChange={e => updateBulkItem(item.id, {
                        title: e.target.value
                      })} onKeyDown={e => handleBulkKeyDown(e, item, index)} placeholder={index === 0 ? 'Type request title...' : 'Next request...'} className={`w-full px-2.5 py-2 border rounded-md text-sm transition-colors focus:ring-1 focus:ring-primary-500 focus:border-primary-500 outline-none ${item.title.trim() ? 'border-primary-300 bg-primary-50/20' : 'border-slate-200'}`} data-id="element-7835" />
                            </div>

                            {/* Override indicators */}
                            {item.title.trim() && (item.category !== 'default' || item.priority !== 'default') && <div className="flex items-center gap-1 flex-shrink-0" data-id="element-7836">
                                  {item.priority !== 'default' && <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold ${{
                        low: 'bg-slate-100 text-slate-600',
                        medium: 'bg-blue-100 text-blue-700',
                        high: 'bg-amber-100 text-amber-700',
                        urgent: 'bg-red-100 text-red-700'
                      }[item.priority] || ''}`} data-id="element-7837">
                                      {priorityConfig[item.priority as TicketPriority]?.label}
                                    </span>}
                                  {item.category !== 'default' && item.category !== '' && <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold bg-slate-100 text-slate-600" data-id="element-7838">
                                        {categoryConfig[item.category as keyof typeof categoryConfig]}
                                      </span>}
                                </div>}

                            {/* Expand toggle */}
                            {item.title.trim() ? <button type="button" onClick={() => updateBulkItem(item.id, {
                      expanded: !item.expanded
                    })} className={`w-7 h-7 flex items-center justify-center rounded transition-all flex-shrink-0 ${item.expanded ? 'text-primary-500 bg-primary-50' : 'text-slate-300 opacity-0 group-hover:opacity-100 hover:text-slate-500 hover:bg-slate-100'}`} tabIndex={-1} title={item.expanded ? 'Collapse details' : 'Add description'} data-id="element-7839">
                                {item.expanded ? <ChevronUp className="h-3.5 w-3.5" data-id="element-7840" /> : <ChevronDown className="h-3.5 w-3.5" data-id="element-7841" />}
                              </button> : <span className="w-7" data-id="element-7842" />}

                            {/* Remove */}
                            <button type="button" onClick={() => removeBulkRow(item.id)} className="w-7 h-7 flex items-center justify-center rounded text-slate-300 opacity-0 group-hover:opacity-100 hover:text-red-500 hover:bg-red-50 transition-all flex-shrink-0" tabIndex={-1} data-id="element-7843">
                              <Trash2 className="h-3.5 w-3.5" data-id="element-7844" />
                            </button>
                          </div>

                          {/* Expanded details */}
                          {item.expanded && item.title.trim() && <div className="ml-6 mr-14 mt-1 mb-2 pl-4 border-l-2 border-primary-200 bg-primary-50/20 rounded-r-md p-3 space-y-3" data-id="element-7845">
                              <div className="grid grid-cols-2 gap-3" data-id="element-7846">
                                <div data-id="element-7847">
                                  <label className="block text-[11px] font-medium text-slate-500 uppercase tracking-wider mb-1" data-id="element-7848">
                                    Category
                                  </label>
                                  <select value={item.category} onChange={e => updateBulkItem(item.id, {
                          category: e.target.value as TicketCategory | 'default'
                        })} className={`w-full px-2 py-1.5 border rounded-md text-sm focus:ring-1 focus:ring-primary-500 focus:border-primary-500 outline-none ${item.category !== 'default' ? 'border-primary-300 bg-white' : 'border-slate-200 bg-white'}`} tabIndex={-1} data-id="element-7849">
                                    <option value="default" data-id="element-7850">
                                      ↳ Use default
                                      {bulkCategory ? ` (${categoryConfig[bulkCategory as keyof typeof categoryConfig] || 'None'})` : ' (None)'}
                                    </option>
                                    <option value="" data-id="element-7851">None</option>
                                    {Object.entries(categoryConfig).map(([key, label]) => <option key={key} value={key} data-id="element-7852">
                                          {label}
                                        </option>)}
                                  </select>
                                </div>
                                <div data-id="element-7853">
                                  <label className="block text-[11px] font-medium text-slate-500 uppercase tracking-wider mb-1" data-id="element-7854">
                                    Priority
                                  </label>
                                  <select value={item.priority} onChange={e => updateBulkItem(item.id, {
                          priority: e.target.value as TicketPriority | 'default'
                        })} className={`w-full px-2 py-1.5 border rounded-md text-sm focus:ring-1 focus:ring-primary-500 focus:border-primary-500 outline-none ${item.priority !== 'default' ? 'border-primary-300 bg-white' : 'border-slate-200 bg-white'}`} tabIndex={-1} data-id="element-7855">
                                    <option value="default" data-id="element-7856">
                                      ↳ Use default (
                                      {priorityConfig[bulkPriority].label})
                                    </option>
                                    {Object.entries(priorityConfig).map(([key, config]) => <option key={key} value={key} data-id="element-7857">
                                          {config.label}
                                        </option>)}
                                  </select>
                                </div>
                              </div>
                              <div data-id="element-7858">
                                <label className="block text-[11px] font-medium text-slate-500 uppercase tracking-wider mb-1" data-id="element-7859">
                                  Description
                                </label>
                                <textarea value={item.description} onChange={e => updateBulkItem(item.id, {
                        description: e.target.value
                      })} rows={2} placeholder="Optional description for this request..." className="w-full px-2.5 py-1.5 border border-slate-200 rounded-md text-sm focus:ring-1 focus:ring-primary-500 focus:border-primary-500 outline-none resize-none" tabIndex={-1} data-id="element-7860" />
                              </div>
                            </div>}
                        </div>)}
                    </div>

                    {/* Keyboard hints */}
                    <div className="flex items-center gap-1.5 mt-3 px-6" data-id="element-7861">
                      <CornerDownLeft className="h-3 w-3 text-slate-300" data-id="element-7862" />
                      <span className="text-[11px] text-slate-400" data-id="element-7863">
                        <kbd className="px-1 py-0.5 bg-slate-100 border border-slate-200 rounded text-[10px] font-mono" data-id="element-7864">
                          Tab
                        </kbd>
                        {' / '}
                        <kbd className="px-1 py-0.5 bg-slate-100 border border-slate-200 rounded text-[10px] font-mono" data-id="element-7865">
                          Enter
                        </kbd>
                        {' add another · '}
                        <kbd className="px-1 py-0.5 bg-slate-100 border border-slate-200 rounded text-[10px] font-mono" data-id="element-7866">
                          ↓
                        </kbd>
                        {' expand details'}
                      </span>
                    </div>
                  </div>

                  {/* Summary */}
                  {validBulkItems.length > 0 && <div className="bg-slate-50 rounded-lg border border-slate-200 px-4 py-3 flex items-center justify-between" data-id="element-7867">
                      <div className="flex items-center gap-3 text-xs" data-id="element-7868">
                        <span className="flex items-center gap-1 text-primary-700" data-id="element-7869">
                          <PlusCircle className="h-3 w-3" data-id="element-7870" />{' '}
                          {validBulkItems.length} request
                          {validBulkItems.length !== 1 ? 's' : ''}
                        </span>
                        <span className="text-slate-400" data-id="element-7871">·</span>
                        <span className="text-slate-600" data-id="element-7872">
                          {validBulkItems.filter(i => i.description.trim()).length}{' '}
                          with descriptions
                        </span>
                        {validBulkItems.filter(i => i.category !== 'default' || i.priority !== 'default').length > 0 && <>
                            <span className="text-slate-400" data-id="element-7873">·</span>
                            <span className="text-slate-600" data-id="element-7874">
                              {validBulkItems.filter(i => i.category !== 'default' || i.priority !== 'default').length}{' '}
                              with overrides
                            </span>
                          </>}
                      </div>
                    </div>}
                </div>}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-3 flex-shrink-0" data-id="element-7875">
              <Button variant="outline" onClick={() => {
            setShowNewModal(false);
            setModalMode('single');
          }} type="button" data-id="element-7876">
                Cancel
              </Button>
              {modalMode === 'single' ? <Button type="submit" form="single-form" disabled={!newRequest.title || !newRequest.description} data-id="element-7877">
                  Submit Request
                </Button> : <Button onClick={handleBulkSubmitAll} disabled={validBulkItems.length === 0} leftIcon={<Zap className="h-4 w-4" data-id="element-7879" />} data-id="element-7878">
                  Submit All ({validBulkItems.length})
                </Button>}
            </div>
          </div>
        </div>}
    </div>;
}